/**
 * Grid Density Control
 * Allows users to choose how many product cards display per row
 * Saves preference to localStorage
 */

class GridDensityControl {
  constructor() {
    this.storageKey = 'shopify_grid_density_preference';
    this.defaultDensity = '4'; // Default to 4 columns
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find all product grids on the page
    this.gridContainers = document.querySelectorAll('.collection, [id*="collection"], .product-grid-container, [class*="product-grid"]');

    if (this.gridContainers.length === 0) {
      console.log('No product grids found on this page');
      return;
    }

    // Add data attribute wrapper to grids if not present
    this.gridContainers.forEach(container => {
      if (!container.classList.contains('product-grid-container')) {
        container.classList.add('product-grid-container');
      }
    });

    // Create and inject the control UI
    this.createControlUI();

    // Load saved preference
    this.loadPreference();

    // Setup event listeners
    this.attachEventListeners();
  }

  createControlUI() {
    // Find the best place to insert the control
    const targetContainer = this.findInsertionPoint();

    if (!targetContainer) {
      console.log('No suitable insertion point found for grid control');
      return;
    }

    // Create the control HTML
    const controlHTML = `
      <div class="grid-density-control" role="toolbar" aria-label="Choose grid density">
        <span class="grid-density-control__label" id="grid-density-label">
          Cards per row:
        </span>
        <div class="grid-density-control__buttons" role="group" aria-labelledby="grid-density-label">
          <button
            type="button"
            class="grid-density-btn"
            data-density="2"
            aria-label="Show 2 cards per row"
            title="Large cards - 2 per row">
            <span class="grid-density-btn__icon">
              <span aria-hidden="true">▢ ▢</span>
              <span>2</span>
            </span>
          </button>
          <button
            type="button"
            class="grid-density-btn"
            data-density="3"
            aria-label="Show 3 cards per row"
            title="Medium cards - 3 per row">
            <span class="grid-density-btn__icon">
              <span aria-hidden="true">▢ ▢ ▢</span>
              <span>3</span>
            </span>
          </button>
          <button
            type="button"
            class="grid-density-btn active"
            data-density="4"
            aria-label="Show 4 cards per row"
            aria-pressed="true"
            title="Default - 4 per row">
            <span class="grid-density-btn__icon">
              <span aria-hidden="true">▢ ▢ ▢ ▢</span>
              <span>4</span>
            </span>
          </button>
          <button
            type="button"
            class="grid-density-btn"
            data-density="5"
            aria-label="Show 5 cards per row"
            title="Compact - 5 per row">
            <span class="grid-density-btn__icon">
              <span aria-hidden="true">▢ ▢ ▢ ▢ ▢</span>
              <span>5</span>
            </span>
          </button>
          <button
            type="button"
            class="grid-density-btn"
            data-density="6"
            aria-label="Show 6 cards per row"
            title="Very compact - 6 per row">
            <span class="grid-density-btn__icon">
              <span aria-hidden="true">▢▢▢▢▢▢</span>
              <span>6</span>
            </span>
          </button>
        </div>
      </div>
    `;

    // Insert the control
    targetContainer.insertAdjacentHTML('afterbegin', controlHTML);

    // Store reference to the control
    this.controlElement = targetContainer.querySelector('.grid-density-control');
  }

  findInsertionPoint() {
    // Try to find collection container
    const collectionSelectors = [
      '.collection',
      '[id*="collection"]',
      '.product-grid-container',
      '[id*="ProductGridContainer"]',
      'main .page-width',
      'main'
    ];

    for (const selector of collectionSelectors) {
      const element = document.querySelector(selector);
      if (element) {
        return element;
      }
    }

    return null;
  }

  attachEventListeners() {
    // Listen for clicks on density buttons
    const buttons = document.querySelectorAll('.grid-density-btn');

    buttons.forEach(button => {
      // Add both click and touchend events for better mobile support
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const density = button.dataset.density;
        this.setDensity(density);
      });

      // Add touch support for mobile devices
      button.addEventListener('touchend', (e) => {
        e.preventDefault();
        const density = button.dataset.density;
        this.setDensity(density);
      }, { passive: false });
    });
  }

  setDensity(density) {
    // Update active button state
    const buttons = document.querySelectorAll('.grid-density-btn');
    buttons.forEach(btn => {
      const isActive = btn.dataset.density === density;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive.toString());
    });

    // Apply density to all grid containers
    this.gridContainers.forEach(container => {
      // Add changing class for animation
      container.setAttribute('data-density-changing', 'true');

      // Set the density
      container.setAttribute('data-density', density);

      // Remove changing class after animation
      setTimeout(() => {
        container.removeAttribute('data-density-changing');
      }, 300);
    });

    // Save preference
    this.savePreference(density);

    // Announce change to screen readers
    this.announceChange(density);
  }

  savePreference(density) {
    try {
      localStorage.setItem(this.storageKey, density);
    } catch (e) {
      console.warn('Could not save grid density preference:', e);
    }
  }

  loadPreference() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        this.setDensity(saved);
      } else {
        // Apply default
        this.setDensity(this.defaultDensity);
      }
    } catch (e) {
      console.warn('Could not load grid density preference:', e);
      this.setDensity(this.defaultDensity);
    }
  }

  announceChange(density) {
    // Create or update live region for screen readers
    let liveRegion = document.getElementById('grid-density-announcement');

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'grid-density-announcement';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = `Grid changed to ${density} cards per row`;
  }
}

// Initialize when script loads
if (typeof window !== 'undefined') {
  window.gridDensityControl = new GridDensityControl();
}
